import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Building2, FileText, AlertCircle } from 'lucide-react';

/**
 * Structured Data Tab
 * 
 * Per user requirements, this tab displays:
 * - Facility Name (EXCLUDING imaging facilities - they don't diagnose)
 * - ICD-10 Codes with corresponding Injury/Diagnosis Name
 * 
 * This is a distilled index derived from the Medical Chronology.
 * Imaging facilities (MRI, X-ray, CT, etc.) are excluded as they provide
 * diagnostic findings/impressions, not diagnoses with ICD-10 codes.
 */
const StructuredDataTab = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium" style={{ color: '#64748B' }}>No structured data available</p>
        <p className="text-sm mt-2" style={{ color: '#94a3b8' }}>
          Please process the documents to generate structured data.
        </p>
      </div>
    );
  }

  const facilities = data.facilities || [];

  // Check if facilities array is empty
  if (facilities.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium" style={{ color: '#64748B' }}>No facility data found</p>
        <p className="text-sm mt-2" style={{ color: '#94a3b8' }}>
          No medical facilities or diagnoses were extracted from the documents.
        </p>
      </div>
    );
  }

  // Helper to check if facility is an imaging center (they don't diagnose - excluded from structured data)
  const isImagingFacility = (facility) => {
    const name = (facility.facility_name || '').toLowerCase();
    const type = (facility.facility_type || '').toLowerCase();
    
    const imagingKeywords = [
      'imaging', 'radiology', 'radiological', 'mri', 'ct scan', 'x-ray', 'xray',
      'ultrasound', 'diagnostic imaging', 'nuclear medicine', 'pet scan', 'mammography'
    ];
    
    // Check if facility type indicates imaging
    if (type.includes('imaging') || type.includes('radiology') || type.includes('diagnostic')) {
      return true;
    }
    
    // Check facility name for imaging keywords
    return imagingKeywords.some(keyword => name.includes(keyword));
  };

  // Helper to get all unique ICD-10 codes with descriptions for a facility
  const getICDCodesForFacility = (facility) => {
    const codesMap = new Map();

    if (facility.visits) {
      facility.visits.forEach(visit => {
        if (visit.diagnosis && visit.diagnosis.length > 0) {
          visit.diagnosis.forEach(dx => {
            if (dx.code && !codesMap.has(dx.code)) {
              codesMap.set(dx.code, {
                code: dx.code,
                description: dx.description || 'See medical records for description'
              });
            }
          });
        }
      });
    }

    // Sort codes alphabetically
    return Array.from(codesMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  };

  // Filter facilities: exclude imaging facilities and only include those with ICD codes
  const treatingFacilities = facilities.filter(f => {
    // Exclude imaging facilities - they don't provide diagnoses
    if (isImagingFacility(f)) {
      return false;
    }
    // Include only facilities with ICD codes
    return getICDCodesForFacility(f).length > 0;
  });

  // Get imaging facilities for separate note
  const imagingFacilities = facilities.filter(f => isImagingFacility(f));

  return (
    <div className="space-y-6">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle style={{ color: '#245451' }}>Structured Data Index</CardTitle>
          <CardDescription style={{ color: '#64748B' }}>
            Treating facilities and ICD-10 codes with corresponding diagnoses
          </CardDescription>
        </CardHeader>
        <CardContent>
          {treatingFacilities.length > 0 ? (
            <div className="space-y-6">
              {treatingFacilities.map((facility, facilityIndex) => {
                const icdCodes = getICDCodesForFacility(facility);

                return (
                  <div key={facilityIndex}>
                    {facilityIndex > 0 && <Separator className="my-6" />}
                    
                    {/* Facility Header - ONLY Facility Name */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(36, 84, 81, 0.15)' }}>
                        <Building2 className="h-5 w-5" style={{ color: '#245451' }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold" style={{ color: '#245451' }}>
                          {facility.facility_name}
                        </h3>
                        {facility.facility_type && (
                          <p className="text-sm" style={{ color: '#64748B' }}>{facility.facility_type}</p>
                        )}
                      </div>
                    </div>

                    {/* ICD-10 Codes with Descriptions */}
                    <div className="ml-16">
                      <h4 className="text-sm font-semibold mb-3" style={{ color: '#64748B' }}>ICD-10 Codes:</h4>
                      <div className="space-y-2">
                        {icdCodes.map((icd, codeIndex) => (
                          <div 
                            key={codeIndex}
                            className="flex items-start gap-3 py-2 px-3 rounded-lg"
                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', border: '1px solid rgba(36, 84, 81, 0.1)' }}
                          >
                            <span 
                              className="px-3 py-1 rounded font-mono text-sm font-bold flex-shrink-0"
                              style={{ backgroundColor: '#FEF3C7', color: '#854D0E' }}
                            >
                              {icd.code}
                            </span>
                            <span style={{ color: '#245451' }}>
                              {icd.description}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto mb-4" style={{ color: '#84a2ac' }} />
              <p style={{ color: '#64748B' }}>No treating facilities with ICD-10 codes found</p>
              <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
                Process medical records to extract diagnosis codes from treating providers
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note about imaging facilities */}
      {imagingFacilities.length > 0 && (
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: '#64748B' }}>
              Diagnostic Imaging Facilities
            </CardTitle>
            <CardDescription className="text-sm" style={{ color: '#94a3b8' }}>
              Imaging centers provide diagnostic findings/impressions, not ICD-10 diagnoses. 
              See Medical Chronology for imaging impressions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {imagingFacilities.map((facility, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2">
                  <Building2 className="h-4 w-4" style={{ color: '#94a3b8' }} />
                  <span style={{ color: '#64748B' }}>{facility.facility_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                    Diagnostic Imaging
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StructuredDataTab;
